import React, { useState } from 'react';
import { OTHER_AUTHORS_BOOKS, BOOK_CATEGORIES } from '../data/booksData';

// ---- Book Cover ----
function BookCover({ book, size = 'md' }) {
  const w = size === 'lg' ? 140 : 100;
  const h = size === 'lg' ? 196 : 140;
  return (
    <div style={{
      width: w, height: h, flexShrink: 0,
      background: book.coverColor,
      borderRadius: '4px 8px 8px 4px',
      position: 'relative', overflow: 'hidden',
      boxShadow: '-4px 4px 12px rgba(0,0,0,0.5)',
    }}>
      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '7px', background: 'rgba(0,0,0,0.3)' }} />
      <div style={{ position: 'absolute', top: '14px', left: '8px', right: '10px', height: '2px', background: book.accentColor, opacity: 0.8 }} />
      <div style={{ position: 'absolute', bottom: '14px', left: '8px', right: '10px', height: '2px', background: book.accentColor, opacity: 0.8 }} />
      <div style={{ position: 'absolute', inset: '22px 10px 22px 14px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        <div style={{ fontFamily: 'var(--font-serif)', fontSize: '10px', fontWeight: 700, color: '#fff', lineHeight: 1.3 }}>
          {book.title}
        </div>
        <div style={{ fontSize: '8px', color: book.accentColor, letterSpacing: '0.05em', textTransform: 'uppercase', fontWeight: 500 }}>
          {book.author}
        </div>
      </div>
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, transparent 60%)', pointerEvents: 'none' }} />
    </div>
  );
}

// ---- Book Card — stacks on mobile ----
function BookCard({ book, onPreview }) {
  const [hovered, setHovered] = useState(false);

  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = book.pdfUrl;
    a.download = `${book.title}.pdf`;
    a.target = '_blank';
    a.click();
  };

  return (
    <>
      <style>{`
        .book-card-inner {
          display: flex;
          flex-direction: row;
          gap: 20px;
          align-items: flex-start;
        }
        .book-btns {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          margin-top: 8px;
        }
        @media (max-width: 520px) {
          .book-card-inner {
            flex-direction: column;
            align-items: center;
            text-align: center;
          }
          .book-btns {
            justify-content: center;
          }
          .book-category-pill {
            margin: 0 auto;
          }
        }
      `}</style>

      <div
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          background: hovered ? 'rgba(201,168,76,0.06)' : 'rgba(255,255,255,0.02)',
          border: '1px solid',
          borderColor: hovered ? 'rgba(201,168,76,0.35)' : 'rgba(201,168,76,0.1)',
          borderRadius: '10px',
          padding: '20px',
          transition: 'all 0.25s ease',
          boxShadow: hovered ? '0 8px 32px rgba(201,168,76,0.1)' : 'none',
          width: '100%',
          overflow: 'hidden',
        }}
      >
        <div className="book-card-inner">
          {/* Cover */}
          <BookCover book={book} />

          {/* Info */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 0, width: '100%' }}>
            <span className="book-category-pill" style={{
              display: 'inline-block', alignSelf: 'flex-start',
              fontSize: '10px', letterSpacing: '0.08em',
              textTransform: 'uppercase', color: 'var(--gold)',
              background: 'rgba(201,168,76,0.1)',
              border: '1px solid rgba(201,168,76,0.2)',
              borderRadius: '100px', padding: '3px 10px',
              whiteSpace: 'nowrap',
            }}>
              {book.category}
            </span>

            <h3 style={{
              fontFamily: 'var(--font-serif)',
              fontSize: 'clamp(15px, 3vw, 18px)',
              fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.2,
            }}>
              {book.title}
            </h3>

            <div style={{ fontSize: '13px', color: 'var(--gold)', fontWeight: 500 }}>
              {book.author}
            </div>

            <p style={{
              fontSize: '13px', color: 'var(--text-secondary)',
              lineHeight: 1.65, fontWeight: 300,
            }}>
              {book.description}
            </p>

            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'flex', gap: '16px', flexWrap: 'wrap', justifyContent: 'center' }}>
              <span>{book.pages} pages</span>
              <span>{book.year}</span>
            </div>

            <div className="book-btns">
              <button onClick={() => onPreview(book)} style={{
                background: 'transparent',
                border: '1px solid rgba(201,168,76,0.3)',
                borderRadius: '4px', padding: '9px 16px',
                color: 'var(--gold)', fontSize: '12px', fontWeight: 500,
                cursor: 'pointer', fontFamily: 'var(--font-sans)',
                letterSpacing: '0.05em', whiteSpace: 'nowrap',
              }}>
                👁 Preview
              </button>
              <button onClick={handleDownload} style={{
                background: 'var(--gold)', color: 'var(--ink)',
                border: 'none', borderRadius: '4px', padding: '9px 16px',
                fontSize: '12px', fontWeight: 500,
                cursor: 'pointer', fontFamily: 'var(--font-sans)',
                letterSpacing: '0.05em', whiteSpace: 'nowrap',
              }}>
                ⬇ Download PDF
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ---- Preview Modal ----
function PreviewModal({ book, onClose }) {
  if (!book) return null;
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.88)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '16px', backdropFilter: 'blur(6px)',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#141720',
        border: '1px solid rgba(201,168,76,0.2)',
        borderRadius: '12px',
        width: '100%', maxWidth: '680px',
        maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        overflow: 'hidden', position: 'relative',
      }}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(201,168,76,0.1)', display: 'flex', gap: '16px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <BookCover book={book} size="lg" />
          <div style={{ flex: 1, minWidth: '180px' }}>
            <span style={{ fontSize: '10px', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--gold)' }}>{book.category}</span>
            <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(16px,3vw,20px)', fontWeight: 700, color: 'var(--text-primary)', marginTop: '4px', lineHeight: 1.2 }}>
              {book.title}
            </h2>
            <div style={{ fontSize: '13px', color: 'var(--gold)', marginTop: '4px' }}>{book.author}</div>
            <p style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.6, marginTop: '8px', fontWeight: 300 }}>{book.description}</p>
            <a href={book.pdfUrl} download={`${book.title}.pdf`} target="_blank" rel="noopener noreferrer" style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              background: 'var(--gold)', color: 'var(--ink)',
              padding: '9px 18px', borderRadius: '4px',
              textDecoration: 'none', fontSize: '13px', fontWeight: 500,
              fontFamily: 'var(--font-sans)', marginTop: '12px',
            }}>
              ⬇ Download PDF
            </a>
          </div>
          <button onClick={onClose} style={{
            position: 'absolute', top: '12px', right: '12px',
            background: 'rgba(255,255,255,0.06)', border: 'none',
            borderRadius: '50%', width: '30px', height: '30px',
            color: 'var(--text-secondary)', fontSize: '17px',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>×</button>
        </div>
        {/* PDF iframe */}
        <div style={{ flex: 1, minHeight: '300px', background: '#0D0F14' }}>
          <iframe src={book.previewUrl} title={`Preview: ${book.title}`}
            style={{ width: '100%', height: '100%', minHeight: '300px', border: 'none' }} />
        </div>
      </div>
    </div>
  );
}

// ---- Main BooksPage ----
export default function BooksPage() {
  const [activeTab, setActiveTab]         = useState('other');
  const [activeCategory, setActiveCategory] = useState('All');
  const [previewBook, setPreviewBook]     = useState(null);
  const [search, setSearch]               = useState('');

  const filtered = OTHER_AUTHORS_BOOKS.filter(b => {
    const matchCat    = activeCategory === 'All' || b.category === activeCategory;
    const matchSearch = b.title.toLowerCase().includes(search.toLowerCase()) ||
                        b.author.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  return (
    <div style={{ minHeight: '100vh', background: 'var(--ink)', paddingTop: '80px', width: '100%', overflowX: 'hidden' }}>

      {/* Page Header */}
      <div style={{
        padding: 'clamp(32px,6vw,60px) 0 32px',
        borderBottom: '1px solid rgba(201,168,76,0.1)',
        background: 'radial-gradient(ellipse 600px 300px at 50% 0%, rgba(201,168,76,0.05) 0%, transparent 70%)',
      }}>
        <div className="container">
          <span style={{ fontSize: '11px', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--gold)' }}>
            PMG Library
          </span>
          <h1 style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 'clamp(28px, 6vw, 56px)',
            fontWeight: 700, color: 'var(--text-primary)',
            marginTop: '8px', lineHeight: 1.1,
          }}>
            Books & Resources
          </h1>
          <p style={{ fontSize: 'clamp(13px,2.5vw,15px)', color: 'var(--text-secondary)', maxWidth: '500px', marginTop: '10px', lineHeight: 1.7, fontWeight: 300 }}>
            Curated reading for investors, traders, and builders. Free PDFs from top authors alongside exclusive PMG publications.
          </p>

          {/* Tabs */}
          <div style={{
            display: 'flex', gap: '4px', marginTop: '28px',
            background: 'rgba(255,255,255,0.04)',
            borderRadius: '8px', padding: '4px', width: 'fit-content',
            flexWrap: 'wrap',
          }}>
            {[
              { key: 'other', label: '📚 Other Authors' },
              { key: 'pmg',   label: '🔒 PMG Books' },
            ].map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
                background: activeTab === tab.key ? 'var(--gold)' : 'transparent',
                color: activeTab === tab.key ? 'var(--ink)' : 'var(--text-secondary)',
                border: 'none', borderRadius: '6px',
                padding: '10px 20px',
                fontSize: 'clamp(12px,2.5vw,13px)',
                fontWeight: 500, cursor: 'pointer',
                fontFamily: 'var(--font-sans)',
                transition: 'all 0.2s', whiteSpace: 'nowrap',
              }}>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* OTHER AUTHORS */}
      {activeTab === 'other' && (
        <div className="container" style={{ padding: 'clamp(24px,4vw,40px) clamp(16px,4vw,48px)' }}>

          {/* Search + filter */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '28px' }}>
            <input
              type="text"
              placeholder="Search books or authors..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%', padding: '11px 14px',
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(201,168,76,0.15)',
                borderRadius: '6px', color: 'var(--text-primary)',
                fontSize: '14px', fontFamily: 'var(--font-sans)', outline: 'none',
                WebkitAppearance: 'none',
              }}
              onFocus={e => e.target.style.borderColor = 'var(--gold)'}
              onBlur={e => e.target.style.borderColor = 'rgba(201,168,76,0.15)'}
            />
            {/* Category pills — horizontally scrollable on mobile */}
            <div style={{
              display: 'flex', gap: '8px',
              overflowX: 'auto', paddingBottom: '4px',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
            }}>
              {BOOK_CATEGORIES.map(cat => (
                <button key={cat} onClick={() => setActiveCategory(cat)} style={{
                  background: activeCategory === cat ? 'rgba(201,168,76,0.15)' : 'transparent',
                  border: `1px solid ${activeCategory === cat ? 'var(--gold)' : 'rgba(201,168,76,0.2)'}`,
                  borderRadius: '100px', padding: '6px 14px',
                  color: activeCategory === cat ? 'var(--gold)' : 'var(--text-secondary)',
                  fontSize: '12px', cursor: 'pointer',
                  fontFamily: 'var(--font-sans)', transition: 'all 0.2s',
                  whiteSpace: 'nowrap', flexShrink: 0,
                }}>
                  {cat}
                </button>
              ))}
            </div>
          </div>

          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
            {filtered.length} book{filtered.length !== 1 ? 's' : ''} found
          </div>

          {/* Book list — single column on mobile, 2 col on desktop */}
          {filtered.length > 0 ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 400px), 1fr))',
              gap: '16px',
            }}>
              {filtered.map(book => (
                <BookCard key={book.id} book={book} onPreview={setPreviewBook} />
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-secondary)' }}>
              <div style={{ fontSize: '36px', marginBottom: '12px' }}>📭</div>
              <div>No books found</div>
            </div>
          )}
        </div>
      )}

      {/* PMG BOOKS */}
      {activeTab === 'pmg' && (
        <div className="container" style={{ padding: '60px 20px', textAlign: 'center' }}>
          <div style={{ maxWidth: '460px', margin: '0 auto' }}>
            <div style={{ fontSize: '52px', marginBottom: '16px' }}>🔒</div>
            <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(22px,5vw,28px)', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '12px' }}>
              PMG Exclusive Books
            </h2>
            <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.75, marginBottom: '28px', fontWeight: 300 }}>
              Original publications by the PMG team — exclusive trading guides, market playbooks, and wealth-building frameworks.
            </p>
            <div style={{
              background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)',
              borderRadius: '10px', padding: '20px',
              fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.7,
            }}>
              🚀 <strong style={{ color: 'var(--text-primary)' }}>Coming Soon</strong> — Join our Telegram to get notified first.
              <br /><br />
              <a href="https://t.me/+nElKNiC_2ac2M2Zk" target="_blank" rel="noopener noreferrer" style={{
                display: 'inline-block', background: 'var(--gold)', color: 'var(--ink)',
                padding: '10px 24px', borderRadius: '4px', textDecoration: 'none',
                fontSize: '13px', fontWeight: 500, fontFamily: 'var(--font-sans)',
              }}>
                Join PMG Telegram
              </a>
            </div>
          </div>
        </div>
      )}

      <PreviewModal book={previewBook} onClose={() => setPreviewBook(null)} />
    </div>
  );
}
